import { FC, useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import ActionButton from 'components/ActionButton'
import ChevronIcon from 'components/ChevronIcon/ChevronIcon'
import { ReactComponent as MarkerRedIcon } from 'assets/icons/marker.svg'
import { ReactComponent as MarkerBlackIcon } from 'assets/icons/marker-black.svg'
import { ReactComponent as Notes } from 'assets/icons/notes.svg'
import {
  ModalCard as SharedModalCard,
  ModalOverlay as SharedModalOverlay,
  ModalTitle
} from './styles'
import SelectionBox from 'components/SelectionBox'
import RichEditor from 'components/Assessment/RichEditor'
import {
  bindDrawingNotes,
  bindEraserNotes,
  eraseDrawingNotes,
  getMarkingsNotes,
  initDrawingNotes,
  setMarkingsNotes,
  unbindDrawingNotes,
  unbindEraserNotes
} from 'util/RaphaelInit'
import {
  ClearAllButton,
  MarkerPopupContainer,
  MarkerPopupWrapper,
  MarkerTrigger,
  PopupBlackDotIcon,
  PopupEraserIcon,
  PopupRedDotIcon,
  PopupRemoveIcon
} from 'components/StudentTools/styles'

type StudentNotesModalProps = {
  visible: boolean
  contextLabel: string
  subtitle: string
  notesHtml: string
  initialPenMarkings?: any
  onNotesChange: (value: string) => void
  onClose: (penPayload?: any) => void
}

type MarkerColor = 'red' | 'black'
type MarkerMode = 'draw' | 'erase' | null

const DEFAULT_WIDTH = 974
const DEFAULT_HEIGHT = 997
const MIN_WIDTH = 520
const MIN_HEIGHT = 320
const VIEWPORT_PADDING = 12

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max)
}

const Overlay = styled(SharedModalOverlay)`
  z-index: 140;
`

const ModalCard = styled(SharedModalCard)<{
  $left: number
  $top: number
  $width: number
  $height: number
}>`
  position: fixed;
  left: ${(props) => `${props.$left}px`};
  top: ${(props) => `${props.$top}px`};
  width: ${(props) => `${props.$width}px`};
  height: ${(props) => `${props.$height}px`};
  min-width: ${MIN_WIDTH}px;
  min-height: ${MIN_HEIGHT}px;
  max-width: none;
  max-height: none;
  border-radius: 8px;
  box-shadow: none;
  border: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const DragHandle = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: move;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`

const ResizeHandle = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 36px;
  height: 36px;
  cursor: nwse-resize;
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    right: 5px;
    bottom: 5px;
    width: 12px;
    height: 12px;
    border-right: 2px solid ${(props) => props.theme.colors.text.paragraph};
    border-bottom: 2px solid ${(props) => props.theme.colors.text.paragraph};
  }
`

const Header = styled.header`
  height: 70px;
  min-height: 70px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const Title = styled(ModalTitle)`
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Subtitle = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 15px;
  font-weight: 400;
  line-height: 1.2;
`

const Content = styled.section`
  flex: 1;
  min-height: 0;
  background: ${(props) => props.theme.colors.card.background};
  position: relative;
`

const ContentViewport = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`

const MarkerCanvas = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
`

const Footer = styled.footer`
  height: 60px;
  min-height: 60px;
  border-top: 1px solid ${(props) => props.theme.colors.border.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
`

const MarkerIconWrap = styled.div`
  display: flex;
  align-items: center;
`

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const StyledMarkerPopupContainer = styled(MarkerPopupContainer)`
  left: 120px;
`

const StudentNotesModal: FC<StudentNotesModalProps> = ({
  visible,
  contextLabel,
  subtitle,
  notesHtml,
  initialPenMarkings,
  onNotesChange,
  onClose
}) => {
  const modalCardRef = useRef<HTMLDivElement | null>(null)
  const interactionRef = useRef<{
    type: 'drag' | 'resize'
    startX: number
    startY: number
    initialLeft: number
    initialTop: number
    initialWidth: number
    initialHeight: number
  } | null>(null)
  const [modalWidth, setModalWidth] = useState(DEFAULT_WIDTH)
  const [modalHeight, setModalHeight] = useState(DEFAULT_HEIGHT)
  const [modalLeft, setModalLeft] = useState(VIEWPORT_PADDING)
  const [modalTop, setModalTop] = useState(VIEWPORT_PADDING)
  const [isMarkerPopupOpen, setIsMarkerPopupOpen] = useState(false)
  const [markerMode, setMarkerMode] = useState<MarkerMode>(null)
  const [selectedMarkerColor, setSelectedMarkerColor] =
    useState<MarkerColor>('red')
  const [isScreenMarked, setIsScreenMarked] = useState(false)
  const [notesHtmlValue, setNotesHtmlValue] = useState(notesHtml || '')

  const modalWidthRef = useRef(DEFAULT_WIDTH)
  const modalHeightRef = useRef(DEFAULT_HEIGHT)
  const drawingInitializedRef = useRef(false)
  const initialMarkingsLoadedRef = useRef(false)
  const markerRef = useRef<any>(null)

  const handler = useCallback((event: any) => {
    const elem = event.target
    if (
      elem.nodeName === 'path' &&
      elem.parentNode.id === 'sketch-container-notes'
    ) {
      elem.remove()
    }
  }, [])

  const isMarkerSelected = markerMode !== null
  const ActiveMarkerIcon =
    selectedMarkerColor === 'black' ? MarkerBlackIcon : MarkerRedIcon

  useEffect(() => {
    modalWidthRef.current = modalWidth
    modalHeightRef.current = modalHeight
  }, [modalWidth, modalHeight])

  useEffect(() => {
    if (!visible) {
      setIsMarkerPopupOpen(false)
      setMarkerMode(null)
      setIsScreenMarked(false)
      drawingInitializedRef.current = false
      initialMarkingsLoadedRef.current = false
      eraseDrawingNotes()
      unbindDrawingNotes()
      unbindEraserNotes()
      return
    }

    setNotesHtmlValue(notesHtml || '')
  }, [visible, notesHtml])

  const initializeDrawingSurface = useCallback(() => {
    if (drawingInitializedRef.current) return true

    const drawingContainer = document.getElementById('sketchContainerNotes')
    if (!drawingContainer) return false

    const rect = drawingContainer.getBoundingClientRect()
    const width = Math.round(drawingContainer.clientWidth || rect.width)
    const height = Math.round(drawingContainer.clientHeight || rect.height)

    if (!width || !height) return false

    initDrawingNotes(width, height)
    drawingInitializedRef.current = true
    return true
  }, [])

  const resolveMarkingsPayload = useCallback((payload: any) => {
    const parsedPayload = payload?.trim?.()
      ? JSON.parse(payload || '{}')
      : payload

    if (Array.isArray(parsedPayload)) {
      return parsedPayload
    }

    return Array.isArray(parsedPayload?.markings) ? parsedPayload.markings : []
  }, [])

  useEffect(() => {
    if (!visible || !isMarkerSelected) {
      document.removeEventListener('click', handler)
      unbindDrawingNotes()
      unbindEraserNotes()
      return
    }

    if (!initializeDrawingSurface()) {
      const rafId = window.requestAnimationFrame(() => {
        initializeDrawingSurface()
      })

      return () => {
        window.cancelAnimationFrame(rafId)
      }
    }

    if (markerMode === 'erase') {
      unbindDrawingNotes()
      bindEraserNotes()
      document.addEventListener('click', handler)
      return
    }

    if (selectedMarkerColor) {
      document.removeEventListener('click', handler)
      bindDrawingNotes(setIsScreenMarked, { markerColor: selectedMarkerColor })
      return
    }

    markerRef.current && unbindDrawingNotes()
  }, [
    handler,
    initializeDrawingSurface,
    isMarkerSelected,
    markerMode,
    selectedMarkerColor,
    setIsScreenMarked,
    visible
  ])

  useEffect(() => {
    if (!visible || initialMarkingsLoadedRef.current) {
      return
    }

    const markings = resolveMarkingsPayload(initialPenMarkings)

    const loadInitialMarkings = () => {
      if (!initializeDrawingSurface()) {
        return false
      }

      // Mirror the modal Load button behavior when applying payloads.
      setMarkingsNotes(markings)
      setIsScreenMarked(markings.length > 0)
      initialMarkingsLoadedRef.current = true
      return true
    }

    if (loadInitialMarkings()) {
      return
    }

    const rafId = window.requestAnimationFrame(() => {
      loadInitialMarkings()
    })

    return () => {
      window.cancelAnimationFrame(rafId)
    }
  }, [
    initializeDrawingSurface,
    initialPenMarkings,
    resolveMarkingsPayload,
    visible
  ])

  const resetToCenteredLayout = useCallback(() => {
    const maxWidth = Math.max(
      MIN_WIDTH,
      window.innerWidth - VIEWPORT_PADDING * 2
    )
    const maxHeight = Math.max(
      MIN_HEIGHT,
      window.innerHeight - VIEWPORT_PADDING * 2
    )
    const nextWidth = Math.min(DEFAULT_WIDTH, maxWidth)
    const nextHeight = Math.min(DEFAULT_HEIGHT, maxHeight)

    setModalWidth(nextWidth)
    setModalHeight(nextHeight)
    setModalLeft(
      clamp(
        (window.innerWidth - nextWidth) / 2,
        VIEWPORT_PADDING,
        window.innerWidth - nextWidth - VIEWPORT_PADDING
      )
    )
    setModalTop(
      clamp(
        (window.innerHeight - nextHeight) / 2,
        VIEWPORT_PADDING,
        window.innerHeight - nextHeight - VIEWPORT_PADDING
      )
    )
  }, [])

  const getCurrentPenPayload = useCallback(() => {
    return {
      version: 1,
      created_at: new Date().toISOString(),
      markings: getMarkingsNotes()
    }
  }, [])

  const closeModalWithPen = useCallback(() => {
    onClose(getCurrentPenPayload())
  }, [getCurrentPenPayload, onClose])

  useEffect(() => {
    if (!visible) return

    resetToCenteredLayout()

    requestAnimationFrame(() => {
      modalCardRef.current?.focus()
    })
  }, [visible, resetToCenteredLayout])

  useEffect(() => {
    if (!visible) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModalWithPen()
        return
      }

      if (event.key === 'Tab' && modalCardRef.current) {
        const focusableElements =
          modalCardRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )

        if (focusableElements.length === 0) {
          return
        }

        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]
        const activeElement = document.activeElement as HTMLElement | null

        if (event.shiftKey && activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        } else if (!event.shiftKey && activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [visible, closeModalWithPen])

  useEffect(() => {
    if (!visible) return
    const modalCardElement = modalCardRef.current

    const onMouseMove = (event: MouseEvent) => {
      const interaction = interactionRef.current
      if (!interaction) return

      const deltaX = event.clientX - interaction.startX
      const deltaY = event.clientY - interaction.startY

      if (interaction.type === 'drag') {
        const maxLeft = Math.max(
          VIEWPORT_PADDING,
          window.innerWidth - modalWidthRef.current - VIEWPORT_PADDING
        )
        const maxTop = Math.max(
          VIEWPORT_PADDING,
          window.innerHeight - modalHeightRef.current - VIEWPORT_PADDING
        )

        setModalLeft(
          clamp(interaction.initialLeft + deltaX, VIEWPORT_PADDING, maxLeft)
        )
        setModalTop(
          clamp(interaction.initialTop + deltaY, VIEWPORT_PADDING, maxTop)
        )
        return
      }

      const maxWidth =
        window.innerWidth - interaction.initialLeft - VIEWPORT_PADDING
      const maxHeight =
        window.innerHeight - interaction.initialTop - VIEWPORT_PADDING

      setModalWidth(
        clamp(interaction.initialWidth + deltaX, MIN_WIDTH, maxWidth)
      )
      setModalHeight(
        clamp(interaction.initialHeight + deltaY, MIN_HEIGHT, maxHeight)
      )
    }

    const onMouseUp = () => {
      interactionRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      if (modalCardElement) {
        modalCardElement.style.cursor = ''
      }
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)

    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      interactionRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      if (modalCardElement) {
        modalCardElement.style.cursor = ''
      }
    }
  }, [visible])

  const onDragHandleMouseDown = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()
    event.stopPropagation()
    interactionRef.current = {
      type: 'drag',
      startX: event.clientX,
      startY: event.clientY,
      initialLeft: modalLeft,
      initialTop: modalTop,
      initialWidth: modalWidth,
      initialHeight: modalHeight
    }
    document.body.style.cursor = 'move'
    document.body.style.userSelect = 'none'
  }

  const startResizeInteraction = (clientX: number, clientY: number) => {
    interactionRef.current = {
      type: 'resize',
      startX: clientX,
      startY: clientY,
      initialLeft: modalLeft,
      initialTop: modalTop,
      initialWidth: modalWidth,
      initialHeight: modalHeight
    }
    document.body.style.cursor = 'nwse-resize'
    document.body.style.userSelect = 'none'
  }

  const onResizeHandleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    startResizeInteraction(event.clientX, event.clientY)
  }

  const onActivateWithKeyboard = (
    event: React.KeyboardEvent,
    action: () => void
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      action()
    }
  }

  const toggleMarkerPopup = () => {
    setIsMarkerPopupOpen((previous) => {
      const next = !previous

      if (next) {
        setMarkerMode('draw')
      } else {
        setMarkerMode(null)
      }

      return next
    })
  }

  const closeMarkerPopup = () => {
    setIsMarkerPopupOpen(false)
    setMarkerMode(null)
    document.removeEventListener('click', handler)
    unbindDrawingNotes()
    unbindEraserNotes()
  }

  const onMarkerColorSelect = (color: MarkerColor) => {
    setSelectedMarkerColor(color)
    setMarkerMode('draw')
  }

  const onEraserSelect = () => {
    setMarkerMode('erase')
    document.addEventListener('click', handler)
  }

  const onClearAllMarkings = () => {
    eraseDrawingNotes()
    setIsScreenMarked(false)
    setMarkerMode('draw')
  }

  if (!visible) {
    return null
  }

  const title = `Notes for ${contextLabel}`

  return (
    <Overlay onClick={closeModalWithPen}>
      <ModalCard
        $left={modalLeft}
        $top={modalTop}
        $width={modalWidth}
        $height={modalHeight}
        ref={modalCardRef}
        tabIndex={-1}
        role='dialog'
        aria-modal='true'
        aria-labelledby='student-notes-modal-title'
        aria-describedby='student-notes-modal-subtitle student-notes-modal-content'
        onClick={(event) => event.stopPropagation()}
      >
        <DragHandle
          type='button'
          onMouseDown={onDragHandleMouseDown}
          aria-label='Move student notes modal'
          title='Move'
        />
        <ResizeHandle
          onMouseDown={onResizeHandleMouseDown}
          aria-label='Resize student notes modal'
          title='Resize'
        />
        <Header
          style={{
            borderBottom: isMarkerSelected ? `1px solid ${'#E0E0E0'}` : 'none'
          }}
        >
          <ChevronIcon
            direction='left'
            size='md'
            onClick={closeModalWithPen}
            ariaLabel='Go back and close student notes modal'
          />
          <Notes />
          <HeaderText>
            <Title id='student-notes-modal-title'>{title}</Title>
            <Subtitle id='student-notes-modal-subtitle'>{subtitle}</Subtitle>
          </HeaderText>
        </Header>

        <Content id='student-notes-modal-content'>
          <ContentViewport>
            <RichEditor
              value={notesHtmlValue}
              disabled={isMarkerSelected}
              onChange={(value) => {
                setNotesHtmlValue(value)
                onNotesChange(value)
              }}
            />

            <MarkerCanvas
              id='sketchContainerNotes'
              style={{
                pointerEvents: isMarkerSelected ? 'auto' : 'none'
              }}
            />
          </ContentViewport>
        </Content>

        <Footer>
          <MarkerIconWrap>
            <MarkerPopupWrapper>
              <MarkerTrigger
                role='button'
                tabIndex={0}
                aria-label='Open marker options'
                onClick={toggleMarkerPopup}
                onKeyDown={(event) =>
                  onActivateWithKeyboard(event, toggleMarkerPopup)
                }
              >
                <SelectionBox isActive={isMarkerPopupOpen}>
                  <ActiveMarkerIcon />
                </SelectionBox>
              </MarkerTrigger>

              {isMarkerPopupOpen && (
                <StyledMarkerPopupContainer>
                  <PopupRedDotIcon
                    role='button'
                    tabIndex={0}
                    aria-label='Select red marker'
                    $isFaded={selectedMarkerColor === 'black'}
                    onClick={() => onMarkerColorSelect('red')}
                    onKeyDown={(event) =>
                      onActivateWithKeyboard(event, () =>
                        onMarkerColorSelect('red')
                      )
                    }
                  />
                  <PopupBlackDotIcon
                    role='button'
                    tabIndex={0}
                    aria-label='Select black marker'
                    $isFaded={selectedMarkerColor === 'red'}
                    onClick={() => onMarkerColorSelect('black')}
                    onKeyDown={(event) =>
                      onActivateWithKeyboard(event, () =>
                        onMarkerColorSelect('black')
                      )
                    }
                  />
                  <PopupEraserIcon
                    role='button'
                    tabIndex={0}
                    aria-label='Enable marker eraser'
                    $disabled={!isScreenMarked}
                    onClick={onEraserSelect}
                    onKeyDown={(event) =>
                      onActivateWithKeyboard(event, onEraserSelect)
                    }
                  />
                  <ClearAllButton onClick={() => onClearAllMarkings()}>
                    Clear All
                  </ClearAllButton>
                  <PopupRemoveIcon
                    role='button'
                    tabIndex={0}
                    aria-label='Close marker options'
                    onClick={closeMarkerPopup}
                    onKeyDown={(event) =>
                      onActivateWithKeyboard(event, closeMarkerPopup)
                    }
                  />
                </StyledMarkerPopupContainer>
              )}
            </MarkerPopupWrapper>
          </MarkerIconWrap>
          <FooterActions>
            <ActionButton
              label='Close'
              variant='info-dark'
              width={70}
              onClick={closeModalWithPen}
            />
          </FooterActions>
        </Footer>
      </ModalCard>
    </Overlay>
  )
}

export default StudentNotesModal
